import { clsx } from 'clsx';
import dayjs from 'dayjs';
import {
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Eye,
  FileText,
  Flag,
  Image as ImageIcon,
  Search,
  Settings,
  X,
  XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { adminService } from '@/services';
import type { AttendanceRecord, PopulatedUser } from '@/types';
import {
  ATTENDANCE_STATUS,
  extractErrorMessage,
  formatDate,
  formatEmployeeId,
  formatMinutes,
  formatNaira,
  formatStatus,
  formatTime,
  getLateStatusColor,
  showErrorToast,
  showSuccessToast,
} from '@/utils';

export function AttendanceManagement() {
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, _setPageSize] = useState(10);
  const [startDate, setStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [lateCategoryFilter, setLateCategoryFilter] = useState<string | undefined>();
  const [selectedRecord, setSelectedRecord] = useState<AttendanceRecord | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [flagReason, setFlagReason] = useState('');

  const [manageDeductionsModalOpen, setManageDeductionsModalOpen] = useState(false);
  const [deductionPardonReasons, setDeductionPardonReasons] = useState<Record<string, string>>({});
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    type: 'pardon' | 'revoke';
    attendanceId: string;
    deductionId: string;
    deduction: {
      _id: string;
      type: string;
      amount: number;
      reason: string;
      pardoned?: boolean;
      pardonReason?: string;
    };
  } | null>(null);
  const isLateClockingIn = selectedRecord?.minutesLate && selectedRecord.minutesLate > 0;
  const isMissedClockIn = selectedRecord?.status === ATTENDANCE_STATUS.MISSED_CLOCK_IN;
  const isMissedClockOut = selectedRecord?.status === ATTENDANCE_STATUS.MISSED_CLOCK_OUT;
  const fetchRecords = async () => {
    setLoading(true);
    try {
      const data = await adminService.getAllAttendance({
        startDate,
        endDate,
        status: statusFilter,
        lateCategory: lateCategoryFilter,
        page,
        limit: pageSize,
      });
      setRecords(data.records);
      setTotal(data.total);
    } catch (error) {
      alert('Failed to fetch attendance records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, [startDate, endDate, statusFilter, lateCategoryFilter, page, pageSize]);

  const handleFlag = async () => {
    if (!selectedRecord || !flagReason) return;

    try {
      await adminService.flagAttendance(selectedRecord._id, flagReason);
      alert('Attendance record flagged');
      setFlagModalOpen(false);
      setFlagReason('');
      fetchRecords();
    } catch (error) {
      alert('Failed to flag attendance');
    }
  };

  const handlePardonClick = (
    attendanceId: string,
    deductionId: string,
    deduction: AttendanceRecord['deductions'][0],
  ) => {
    const reason = deductionPardonReasons[deductionId] || '';
    if (reason.trim().length < 3) {
      showErrorToast('Pardon reason must be at least 3 characters');
      return;
    }
    setPendingAction({
      type: 'pardon',
      attendanceId,
      deductionId,
      deduction,
    });
    setConfirmModalOpen(true);
  };

  const handleRevokeClick = (
    attendanceId: string,
    deductionId: string,
    deduction: AttendanceRecord['deductions'][0],
  ) => {
    setPendingAction({
      type: 'revoke',
      attendanceId,
      deductionId,
      deduction,
    });
    setConfirmModalOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!pendingAction) return;

    try {
      if (pendingAction.type === 'pardon') {
        const reason = deductionPardonReasons[pendingAction.deductionId] || '';
        await adminService.pardonDeduction(
          pendingAction.attendanceId,
          pendingAction.deductionId,
          reason,
        );
        showSuccessToast('Deduction pardoned successfully');
      } else {
        await adminService.revokePardon(pendingAction.attendanceId, pendingAction.deductionId);
        showSuccessToast('Pardon revoked successfully');
      }
      setConfirmModalOpen(false);
      const actionAttendanceId = pendingAction.attendanceId;
      const actionDeductionId = pendingAction.deductionId;
      const actionType = pendingAction.type;
      setPendingAction(null);
      fetchRecords();
      if (selectedRecord?._id === actionAttendanceId) {
        const updated = await adminService.getAttendanceById(actionAttendanceId);
        setSelectedRecord(updated);
        if (actionType === 'pardon') {
          setDeductionPardonReasons((prev) => {
            const next = { ...prev };
            delete next[actionDeductionId];
            return next;
          });
        }
      }
    } catch (error) {
      showErrorToast(
        extractErrorMessage(
          error,
          `Failed to ${pendingAction.type === 'pardon' ? 'pardon' : 'revoke pardon'} deduction`,
        ),
      );
    }
  };

  const getUser = (record: AttendanceRecord): PopulatedUser | null => {
    if (typeof record.userId === 'object' && record.userId !== null) {
      return record.userId;
    }
    return null;
  };

  const hasPardonedDeduction = (record: AttendanceRecord): boolean =>
    record.deductions.some((deduction) => deduction.pardoned);

  const selectedAttendanceDeductions = selectedRecord
    ? selectedRecord.deductions.filter((d) => !d.deletedAt)
    : [];

  return (
    <div className="space-y-3">
      <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
        Attendance Registry
      </h1>

      <div className="glass-card p-3 rounded-xl bg-white dark:bg-neolab-forest border border-neolab-green/5 dark:border-white/5 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 items-end">
          <div className="space-y-1 sm:col-span-2 lg:col-span-1">
            <label
              htmlFor="start-date"
              className="text-xs font-bold text-slate-500 uppercase tracking-wider"
            >
              Date Range
            </label>
            <div className="flex gap-1.5">
              <input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input-field py-1.5 px-2 text-xs flex-1 min-w-0"
              />
              <input
                id="end-date"
                aria-label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="input-field py-1.5 px-2 text-xs flex-1 min-w-0"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label
              htmlFor="status-filter"
              className="text-xs font-bold text-slate-500 uppercase tracking-wider"
            >
              Status
            </label>
            <select
              id="status-filter"
              value={statusFilter || ''}
              onChange={(e) => setStatusFilter(e.target.value || undefined)}
              className="input-field py-1.5 px-2 text-xs w-full"
            >
              <option value="">All Statuses</option>
              <option value="clocked_in">Clocked In</option>
              <option value="clocked_out">Clocked Out</option>
              <option value="incomplete">Incomplete</option>
            </select>
          </div>

          <div className="space-y-1">
            <label
              htmlFor="lateness-filter"
              className="text-xs font-bold text-slate-500 uppercase tracking-wider"
            >
              Lateness
            </label>
            <select
              id="lateness-filter"
              value={lateCategoryFilter || ''}
              onChange={(e) => setLateCategoryFilter(e.target.value || undefined)}
              className="input-field py-1.5 px-2 text-xs w-full"
            >
              <option value="">All Lateness</option>
              <option value="on_time">On Time</option>
              <option value="late">Late</option>
              <option value="very_late">Very Late</option>
            </select>
          </div>

          <button
            type="button"
            onClick={fetchRecords}
            className="btn-primary py-1.5 px-3 h-[36px] text-xs w-full sm:w-auto"
          >
            <Search className="w-3.5 h-3.5 mr-1.5" />
            Search
          </button>
        </div>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-neolab-green/[0.02] dark:bg-white/5 border-b border-neolab-green/10 dark:border-white/10">
                <th className="p-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Date
                </th>
                <th className="p-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Employee
                </th>
                <th className="p-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Clock In
                </th>
                <th className="p-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Clock Out
                </th>
                <th className="p-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Status
                </th>
                <th className="p-2 text-[10px] font-black text-slate-500 uppercase tracking-widest text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center">
                    <div className="flex justify-center">
                      <div className="w-6 h-6 border-2 border-neolab-green border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-slate-400 text-sm">
                    No records found
                  </td>
                </tr>
              ) : (
                records.map((record) => {
                  const user = getUser(record);
                  const formattedClockInTimestamp = record.clockIn?.timestamp
                    ? formatTime(record.clockIn.timestamp)
                    : '--:--';
                  const formattedClockOutTimestamp = record.clockOut?.timestamp
                    ? formatTime(record.clockOut.timestamp)
                    : '--:--';
                  return (
                    <tr
                      key={record._id}
                      className="border-b border-neolab-green/[0.02] dark:border-white/5 hover:bg-neolab-green/[0.02] dark:hover:bg-white/5 transition-colors"
                    >
                      <td className="p-2 font-bold text-sm">
                        {formatDate(record.date, 'MMM d, yyyy')}
                      </td>
                      <td className="p-2">
                        <div className="font-bold text-sm">
                          {user ? `${user.profile.firstName} ${user.profile.lastName}` : '-'}
                        </div>
                        <div className="text-[10px] text-slate-400">
                          {formatEmployeeId(user?.employeeId)}
                        </div>
                      </td>
                      <td className="p-2 font-mono text-xs">{formattedClockInTimestamp}</td>
                      <td className="p-2 font-mono text-xs">{formattedClockOutTimestamp}</td>
                      <td className="p-2">
                        <div className="flex gap-1.5">
                          <span
                            className={clsx(
                              'px-1.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider',
                              getLateStatusColor(record.lateCategory) === 'green'
                                ? 'bg-green-100 text-green-700'
                                : getLateStatusColor(record.lateCategory) === 'orange'
                                  ? 'bg-orange-100 text-orange-700'
                                  : 'bg-red-100 text-red-700',
                            )}
                          >
                            {formatStatus(record.lateCategory)}
                          </span>
                          {record.flagged && (
                            <span className="bg-red-500 text-white px-1.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider">
                              Flagged
                            </span>
                          )}
                          {hasPardonedDeduction(record) && (
                            <span className="bg-green-500 text-white px-1.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider">
                              Pardoned
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-2">
                        <div className="flex justify-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedRecord(record);
                              setDetailsModalOpen(true);
                            }}
                            className="p-1.5 hover:bg-neolab-green/10 text-neolab-green rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedRecord(record);
                              setFlagModalOpen(true);
                            }}
                            disabled={record.flagged}
                            className="p-1.5 hover:bg-orange-500/10 text-orange-500 rounded-lg transition-colors disabled:opacity-30"
                            title="Flag Record"
                          >
                            <Flag className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedRecord(record);
                              setManageDeductionsModalOpen(true);
                              const reasons: Record<string, string> = {};
                              record.deductions
                                .filter((d) => !d.deletedAt && !d.pardoned)
                                .forEach((d) => {
                                  reasons[d._id] = '';
                                });
                              setDeductionPardonReasons(reasons);
                            }}
                            className="p-1.5 hover:bg-blue-500/10 text-blue-500 rounded-lg transition-colors"
                            title="Manage Deductions"
                          >
                            <Settings className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="p-2 bg-neolab-green/[0.02] dark:bg-white/5 flex items-center justify-between">
          <div className="text-xs text-slate-500 font-bold">Total {total} records</div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="p-1.5 rounded-lg hover:bg-white dark:hover:bg-white/10 disabled:opacity-30 transition-all"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="font-black px-2 text-xs">
              Page {page} of {Math.ceil(total / pageSize)}
            </span>
            <button
              type="button"
              disabled={page >= Math.ceil(total / pageSize)}
              onClick={() => setPage((p) => p + 1)}
              className="p-1.5 rounded-lg hover:bg-white dark:hover:bg-white/10 disabled:opacity-30 transition-all"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {detailsModalOpen && selectedRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-neolab-deep-green/60 backdrop-blur-sm">
          <div className="glass-card w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white dark:bg-neolab-deep-green p-4 rounded-xl relative">
            <button
              type="button"
              onClick={() => setDetailsModalOpen(false)}
              className="absolute top-6 right-6 p-2 hover:bg-slate-100 dark:hover:bg-white/10 rounded-full transition-all"
            >
              <X className="w-6 h-6" />
            </button>

            <h2 className="text-2xl font-black mb-8 flex items-center gap-3">
              <FileText className="w-6 h-6 text-neolab-green" />
              Attendance Details
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs font-black text-slate-400 uppercase tracking-widest block mb-1">
                      Date
                    </span>
                    <div className="font-bold">
                      {formatDate(selectedRecord.date, 'EEEE, MMMM d, yyyy')}
                    </div>
                  </div>
                  <div>
                    <span className="text-xs font-black text-slate-400 uppercase tracking-widest block mb-1">
                      Employee
                    </span>
                    <div className="font-bold">
                      {getUser(selectedRecord)?.profile.firstName}{' '}
                      {getUser(selectedRecord)?.profile.lastName}
                    </div>
                  </div>
                  <div>
                    <span className="text-xs font-black text-slate-400 uppercase tracking-widest block mb-1">
                      Scheduled Time
                    </span>
                    <div className="font-bold flex items-center gap-2">
                      <Clock className="w-3 h-3 text-slate-400" />
                      {selectedRecord.scheduledClockInTime}
                    </div>
                  </div>
                  <div>
                    <span className="text-xs font-black text-slate-400 uppercase tracking-widest block mb-1">
                      Actual Clock In
                    </span>
                    <div className="font-bold flex items-center gap-2">
                      <Clock className="w-3 h-3 text-neolab-green" />
                      {selectedRecord.clockIn ? formatTime(selectedRecord.clockIn.timestamp) : '--:--'}
                    </div>
                  </div>
                  <div>
                    <span className="text-xs font-black text-slate-400 uppercase tracking-widest block mb-1">
                      Clock Out
                    </span>
                    <div className="font-bold flex items-center gap-2">
                      <Clock className="w-3 h-3 text-orange-500" />
                      {selectedRecord.clockOut
                        ? formatTime(selectedRecord.clockOut.timestamp)
                        : '--:--'}
                    </div>
                  </div>
                  <div>
                    <span className="text-xs font-black text-slate-400 uppercase tracking-widest block mb-1">
                      Status
                    </span>
                    <div
                      className={clsx(
                        'font-bold',
                        isLateClockingIn || isMissedClockIn || isMissedClockOut
                          ? 'text-red-500'
                          : 'text-green-500',
                      )}
                    >
                      {isLateClockingIn
                        ? `Late by ${formatMinutes(selectedRecord.minutesLate)}`
                        : isMissedClockIn
                          ? 'Missed Clock In'
                          : isMissedClockOut
                            ? 'Missed Clock Out'
                            : 'On Time'}
                    </div>
                  </div>
                </div>

                {selectedRecord.clockIn?.photo && (
                  <div className="space-y-2">
                    <span className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                      <ImageIcon className="w-3 h-3" />
                      Clock In Photo
                    </span>
                    <div className="rounded-2xl overflow-hidden border-4 border-slate-100 dark:border-white/10 shadow-lg">
                      <img
                        src={selectedRecord.clockIn.photo}
                        alt="Clock In"
                        className="w-full h-auto"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-8">
                <div className="space-y-4">
                  <span className="text-xs font-black text-slate-400 uppercase tracking-widest block">
                    Deductions
                  </span>
                  {selectedRecord.deductions.length === 0 ? (
                    <div className="p-4 bg-neolab-green/5 dark:bg-white/5 rounded-2xl text-sm text-slate-400 font-bold text-center">
                      No deductions for this record
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {selectedRecord.deductions
                        .filter((d) => !d.deletedAt)
                        .map((deduction) => (
                          <div
                            key={deduction._id}
                            className={`p-4 rounded-2xl flex justify-between items-center group border-2 ${
                              deduction.pardoned
                                ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
                                : 'bg-neolab-green/5 dark:bg-white/5 border-neolab-green/10 dark:border-white/10'
                            }`}
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <div className="font-black text-sm uppercase tracking-wider">
                                  {formatStatus(deduction.type)}
                                </div>
                                {deduction.pardoned && (
                                  <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-black rounded-full uppercase tracking-wider">
                                    Pardoned
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-slate-500 font-bold">
                                {deduction.reason}
                              </div>
                              <div className="text-red-500 font-black mt-1">
                                {formatNaira(deduction.amount)}
                              </div>
                              {deduction.pardoned && deduction.pardonReason && (
                                <div className="mt-2 p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                  <div className="text-xs font-black text-green-700 dark:text-green-300 uppercase tracking-wider mb-1">
                                    Pardon Reason:
                                  </div>
                                  <div className="text-xs text-green-800 dark:text-green-200 font-bold">
                                    {deduction.pardonReason}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>

                {selectedRecord.notes && (
                  <div className="space-y-2">
                    <span className="text-xs font-black text-slate-400 uppercase tracking-widest block">
                      Notes
                    </span>
                    <div className="p-4 bg-neolab-green/5 dark:bg-white/5 rounded-2xl text-sm font-bold">
                      {selectedRecord.notes}
                    </div>
                  </div>
                )}

                {selectedRecord.flagged && (
                  <div className="p-4 bg-red-500/10 border-2 border-red-500/20 rounded-2xl flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs font-black text-red-500 uppercase tracking-widest">
                        Flagged
                      </div>
                      <div className="text-sm font-bold text-red-700 dark:text-red-400">
                        {selectedRecord.flaggedReason}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {flagModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-neolab-deep-green/60 backdrop-blur-sm">
          <div className="glass-card w-full max-w-md bg-white dark:bg-neolab-deep-green p-4 rounded-xl">
            <h3 className="text-lg font-black mb-3 flex items-center gap-2">
              <Flag className="w-6 h-6 text-orange-500" />
              Flag Record
            </h3>
            <p className="text-slate-500 font-bold mb-6">
              Please provide a reason for flagging this attendance record:
            </p>
            <textarea
              rows={4}
              value={flagReason}
              onChange={(e) => setFlagReason(e.target.value)}
              placeholder="Enter reason..."
              className="input-field mb-6 resize-none"
            />
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => {
                  setFlagModalOpen(false);
                  setFlagReason('');
                }}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleFlag}
                disabled={!flagReason}
                className="btn-primary flex-1 bg-red-500 border-red-700"
              >
                Flag
              </button>
            </div>
          </div>
        </div>
      )}

      {manageDeductionsModalOpen && selectedRecord && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-neolab-deep-green/60 backdrop-blur-sm">
          <div className="glass-card w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white dark:bg-neolab-deep-green p-6 rounded-xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-black flex items-center gap-2">
                <Settings className="w-6 h-6 text-blue-500" />
                Manage Deductions
              </h3>
              <button
                type="button"
                onClick={() => {
                  setManageDeductionsModalOpen(false);
                  setDeductionPardonReasons({});
                }}
                className="p-2 hover:bg-slate-100 dark:hover:bg-white/10 rounded-full transition-all"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-4 p-4 bg-neolab-green/5 dark:bg-white/5 rounded-xl">
              <div className="text-sm font-bold text-slate-600 dark:text-slate-400 mb-1">
                {formatDate(selectedRecord.date, 'EEEE, MMMM d, yyyy')}
              </div>
              <div className="font-black text-slate-900 dark:text-white">
                {getUser(selectedRecord)
                  ? `${getUser(selectedRecord)?.profile.firstName} ${getUser(selectedRecord)?.profile.lastName}`
                  : 'Unknown User'}
              </div>
            </div>

            {selectedAttendanceDeductions.length === 0 ? (
              <div className="p-12 text-center text-slate-400">
                No deductions for this attendance record
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-neolab-green/[0.02] dark:bg-white/5 border-b border-neolab-green/10 dark:border-white/10">
                      <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-widest">
                        Deduction
                      </th>
                      <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-widest">
                        Pardon Reason
                      </th>
                      <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-widest">
                        Status
                      </th>
                      <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-widest text-center">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedAttendanceDeductions.map((deduction) => {
                      const deductionKey = deduction._id;
                      const pardonReasonValue = deductionPardonReasons[deductionKey] || '';
                      return (
                        <tr
                          key={deductionKey}
                          className={`border-b border-neolab-green/[0.02] dark:border-white/5 hover:bg-neolab-green/[0.02] dark:hover:bg-white/5 transition-colors ${
                            deduction.pardoned ? 'bg-green-50/50 dark:bg-green-900/10' : ''
                          }`}
                        >
                          <td className="p-4">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="font-black text-sm uppercase tracking-wider">
                                  {formatStatus(deduction.type)}
                                </span>
                              </div>
                              <div className="text-xs text-slate-500 font-bold">
                                {deduction.reason}
                              </div>
                              <div className="text-red-500 font-black">
                                {formatNaira(deduction.amount)}
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            {deduction.pardoned && deduction.pardonReason ? (
                              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                <div className="text-xs text-green-800 dark:text-green-200 font-bold">
                                  {deduction.pardonReason}
                                </div>
                              </div>
                            ) : (
                              <textarea
                                rows={2}
                                value={pardonReasonValue}
                                onChange={(e) =>
                                  setDeductionPardonReasons((prev) => ({
                                    ...prev,
                                    [deductionKey]: e.target.value,
                                  }))
                                }
                                placeholder="Enter pardon reason..."
                                className="input-field resize-none text-sm w-full"
                              />
                            )}
                          </td>
                          <td className="p-4">
                            <div
                              className={clsx(
                                'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider',
                                deduction.pardoned
                                  ? 'bg-green-500/10 text-green-600'
                                  : 'bg-rose-500/10 text-rose-600',
                              )}
                            >
                              <div
                                className={clsx(
                                  'w-1 h-1 rounded-full',
                                  deduction.pardoned ? 'bg-green-500' : 'bg-rose-500',
                                )}
                              />
                              {deduction.pardoned ? 'Pardoned' : 'Unpardoned'}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex justify-center gap-2">
                              {!deduction.pardoned ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    handlePardonClick(selectedRecord._id, deductionKey, deduction)
                                  }
                                  disabled={pardonReasonValue.trim().length < 3}
                                  className="p-2 text-green-600 hover:bg-green-500/10 rounded-xl transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                                  title="Pardon Deduction"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleRevokeClick(selectedRecord._id, deductionKey, deduction)
                                  }
                                  className="p-2 text-orange-600 hover:bg-orange-500/10 rounded-xl transition-all"
                                  title="Revoke Pardon"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {confirmModalOpen && pendingAction && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-neolab-deep-green/60 backdrop-blur-sm">
          <div className="glass-card w-full max-w-md bg-white dark:bg-neolab-deep-green p-6 rounded-xl">
            <h3 className="text-lg font-black mb-4 flex items-center gap-2">
              {pendingAction.type === 'pardon' ? (
                <CheckCircle className="w-6 h-6 text-green-500" />
              ) : (
                <XCircle className="w-6 h-6 text-orange-500" />
              )}
              {pendingAction.type === 'pardon' ? 'Pardon Deduction' : 'Revoke Pardon'}
            </h3>

            <div className="space-y-4 mb-6">
              <div className="p-4 bg-neolab-green/5 dark:bg-white/5 rounded-2xl">
                <div className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">
                  Deduction Type
                </div>
                <div className="font-black text-sm uppercase tracking-wider mb-2">
                  {formatStatus(pendingAction.deduction.type)}
                </div>
                <div className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">
                  Amount
                </div>
                <div className="text-red-500 font-black">
                  {formatNaira(pendingAction.deduction.amount)}
                </div>
                {pendingAction.type === 'pardon' && (
                  <>
                    <div className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1 mt-2">
                      Pardon Reason
                    </div>
                    <div className="text-sm font-bold text-slate-700 dark:text-slate-300">
                      {deductionPardonReasons[pendingAction.deductionId] || ''}
                    </div>
                  </>
                )}
              </div>

              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-800 rounded-2xl">
                <div className="text-sm font-bold text-amber-700 dark:text-amber-300">
                  {pendingAction.type === 'pardon'
                    ? 'Are you sure you want to pardon this deduction? This will exclude it from payment calculations.'
                    : 'Are you sure you want to revoke the pardon? This deduction will be included in payment calculations again.'}
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => {
                  setConfirmModalOpen(false);
                  setPendingAction(null);
                }}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmAction}
                className={`btn-primary flex-1 ${
                  pendingAction.type === 'pardon'
                    ? 'bg-green-500 border-green-700'
                    : 'bg-orange-500 border-orange-700'
                }`}
              >
                {pendingAction.type === 'pardon' ? 'Confirm Pardon' : 'Confirm Revoke'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AttendanceManagement;
